import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faLightbulb,
    faEdit,
    faTrash,
    faPlus,
    faSearch,
    faQuestion,
    faBook,
    faFileAlt,
    faChartLine,
    faNewspaper,
    faGlobe,
    faCog,
    faInfoCircle,
    faExclamationCircle,
    faCheckCircle,
    faTimesCircle,
    faCog as faSettings,
    faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { daireService } from '../../services/daireService';
import './DaireManage.css';

// Predefined icons list
const availableIcons = [
    { name: 'search', icon: faSearch, label: 'Search' },
    { name: 'question', icon: faQuestion, label: 'Question' },
    { name: 'book', icon: faBook, label: 'Book' },
    { name: 'file', icon: faFileAlt, label: 'File' },
    { name: 'chart', icon: faChartLine, label: 'Chart' },
    { name: 'news', icon: faNewspaper, label: 'News' },
    { name: 'globe', icon: faGlobe, label: 'Globe' },
    { name: 'settings', icon: faCog, label: 'Settings' },
    { name: 'info', icon: faInfoCircle, label: 'Info' },
    { name: 'warning', icon: faExclamationCircle, label: 'Warning' },
    { name: 'success', icon: faCheckCircle, label: 'Success' },
    { name: 'error', icon: faTimesCircle, label: 'Error' }
];

// ReactQuill configuration for disclaimers
const quillModules = {
    toolbar: [
        ['bold', 'italic'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['clean']
    ]
};

const quillFormats = [
    'bold', 'italic', 'list', 'bullet'
];

function DaireManage({ currentUser }) {
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingSuggestion, setEditingSuggestion] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showIconSelector, setShowIconSelector] = useState(false);
    const [isCustomIcon, setIsCustomIcon] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        query: '',
        icon: '',
        published: true
    });
    const [options, setOptions] = useState([]);
    const [editingOption, setEditingOption] = useState(null);
    const [optionValue, setOptionValue] = useState('');
    const [showPlaceholderHelp, setShowPlaceholderHelp] = useState(false);

    useEffect(() => {
        fetchSuggestions();
        fetchOptions();
    }, []);

    const fetchSuggestions = async () => {
        try {
            setLoading(true);
            const data = await daireService.getSuggestions();
            setSuggestions(data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch suggestions');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchOptions = async () => {
        try {
            const data = await daireService.getOptions();
            setOptions(data);
        } catch (err) {
            setError('Failed to fetch options');
            console.error(err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingSuggestion) {
                await daireService.updateSuggestion(editingSuggestion.id, formData);
            } else {
                await daireService.createSuggestion(formData);
            }
            setFormData({ title: '', query: '', icon: '', published: true });
            setEditingSuggestion(null);
            setShowAddForm(false);
            fetchSuggestions();
        } catch (err) {
            setError('Failed to save suggestion');
            console.error(err);
        }
    };

    const handleEdit = (suggestion) => {
        setEditingSuggestion(suggestion);
        setFormData({
            title: suggestion.title,
            query: suggestion.query,
            icon: suggestion.icon,
            published: suggestion.published
        });
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this suggestion?')) {
            try {
                await daireService.deleteSuggestion(id);
                fetchSuggestions();
            } catch (err) {
                setError('Failed to delete suggestion');
                console.error(err);
            }
        }
    };

    const handleIconSelect = (iconName) => {
        setFormData(prev => ({
            ...prev,
            icon: iconName
        }));
        setShowIconSelector(false);
        setIsCustomIcon(false);
    };

    const handleCustomIcon = (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            icon: value
        }));
        setIsCustomIcon(true);
    };

    const getIconComponent = (iconName) => {
        if (!iconName) return null;
        const icon = availableIcons.find(i => i.name === iconName);
        if (icon) {
            return <FontAwesomeIcon icon={icon.icon} />;
        }
        return <span className="custom-icon-text">{iconName}</span>;
    };

    const handleOptionEdit = (option) => {
        setEditingOption(option);
        setOptionValue(option.value);
    };

    const handleOptionSave = async () => {
        try {
            await daireService.updateOption(editingOption.id, optionValue);
            setEditingOption(null);
            fetchOptions();
        } catch (err) {
            setError('Failed to update option');
            console.error(err);
        }
    };

    const handleOptionCancel = () => {
        setEditingOption(null);
        setOptionValue('');
    };

    const renderPlaceholderHelp = () => (
        <div className="placeholder-help">
            <button 
                className="help-icon-button"
                onClick={() => setShowPlaceholderHelp(!showPlaceholderHelp)}
                title="Show available placeholders"
            >
                <FontAwesomeIcon icon={faQuestionCircle} />
            </button>
            {showPlaceholderHelp && (
                <div className="placeholder-popover">
                    <div className="placeholder-popover-content">
                        <h4>Available Placeholders</h4>
                        <ul>
                            <li><code>'MemberFirstName'</code> - Member's first name</li>
                            <li><code>'MemberLastName'</code> - Member's last name</li>
                            <li><code>'MemberFullName'</code> - Member's full name</li>
                            <li><code>'CurrentMonth'</code> - Current month</li>
                            <li><code>'CurrentYear'</code> - Current year</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );

    const formatWelcomeMessage = (message, user) => {
        if (!message) return '';
        
        const formattedName = user?.name ? 
            user.name.split(' ')[0].charAt(0).toUpperCase() + user.name.split(' ')[0].slice(1) : 
            'there';

        const currentDate = new Date();
        const currentMonth = currentDate.toLocaleString('default', { month: 'long' });
        const currentYear = currentDate.getFullYear();

        return message
            .replace('MemberFirstName', formattedName)
            .replace('MemberLastName', user?.name?.split(' ')[1] || '')
            .replace('MemberFullName', user?.name || 'there')
            .replace('CurrentMonth', currentMonth)
            .replace('CurrentYear', currentYear);
    };

    return (
        <div className="daire-manage">
            <div className="manage-section">
                <div className="section-header">
                    <h2>
                        <FontAwesomeIcon icon={faLightbulb} className="section-icon" />
                        Daire Suggestion Boxes
                    </h2>
                    <div className="header-actions">
                        {renderPlaceholderHelp()}
                    </div>
                </div>
                <p className="section-description">
                    Manage the suggestion boxes and helper text that appears in the Daire chat interface.
                </p>

                {error && <div className="error-message">{error}</div>}

                <button 
                    className="add-button"
                    onClick={() => {
                        setShowAddForm(true);
                        setEditingSuggestion(null);
                        setFormData({ title: '', query: '', icon: '', published: true });
                    }}
                >
                    <FontAwesomeIcon icon={faPlus} /> Add New Suggestion
                </button>

                {showAddForm && (
                    <form className="suggestion-form" onSubmit={handleSubmit}>
                        <h3>{editingSuggestion ? 'Edit Suggestion' : 'Add New Suggestion'}</h3>
                        <div className="form-group">
                            <label>Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Query</label>
                            <textarea
                                name="query"
                                value={formData.query}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Icon</label>
                            <div className="icon-selector-container">
                                <div className="icon-input-group">
                                    <button 
                                        type="button" 
                                        className="icon-selector-button"
                                        onClick={() => setShowIconSelector(!showIconSelector)}
                                    >
                                        {formData.icon && !isCustomIcon ? (
                                            <>
                                                {getIconComponent(formData.icon)}
                                                <span>{availableIcons.find(i => i.name === formData.icon)?.label}</span>
                                            </>
                                        ) : (
                                            <span>Select an icon</span>
                                        )}
                                    </button>
                                    <input
                                        type="text"
                                        className="custom-icon-input"
                                        placeholder="Or enter custom icon name"
                                        value={isCustomIcon ? formData.icon : ''}
                                        onChange={handleCustomIcon}
                                        onClick={() => {
                                            setShowIconSelector(false);
                                            setIsCustomIcon(true);
                                        }}
                                    />
                                </div>
                                {showIconSelector && (
                                    <div className="icon-selector-dropdown">
                                        <div className="icon-selector-header">
                                            <span>Predefined Icons</span>
                                            <button 
                                                type="button"
                                                className="use-custom-button"
                                                onClick={() => {
                                                    setShowIconSelector(false);
                                                    setIsCustomIcon(true);
                                                }}
                                            >
                                                Use Custom
                                            </button>
                                        </div>
                                        {availableIcons.map(icon => (
                                            <button
                                                key={icon.name}
                                                type="button"
                                                className={`icon-option ${formData.icon === icon.name && !isCustomIcon ? 'selected' : ''}`}
                                                onClick={() => handleIconSelect(icon.name)}
                                            >
                                                <FontAwesomeIcon icon={icon.icon} />
                                                <span>{icon.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="form-group checkbox">
                            <label>
                                <input
                                    type="checkbox"
                                    name="published"
                                    checked={formData.published}
                                    onChange={handleInputChange}
                                />
                                Published
                            </label>
                        </div>
                        <div className="form-actions">
                            <button type="submit" className="save-button">
                                {editingSuggestion ? 'Update' : 'Save'}
                            </button>
                            <button 
                                type="button" 
                                className="cancel-button"
                                onClick={() => {
                                    setShowAddForm(false);
                                    setEditingSuggestion(null);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                {loading ? (
                    <div className="loading">Loading suggestions...</div>
                ) : (
                    <div className="suggestions-list">
                        {suggestions.map(suggestion => (
                            <div key={suggestion.id} className="suggestion-box">
                                <div className="suggestion-header">
                                    <div className="suggestion-title">
                                        {suggestion.icon && getIconComponent(suggestion.icon)}
                                        <h3>{suggestion.title}</h3>
                                    </div>
                                    <span className={`status-tag ${suggestion.published ? 'published' : 'draft'}`}>
                                        {suggestion.published ? 'Published' : 'Draft'}
                                    </span>
                                    <div className="suggestion-actions">
                                        <button 
                                            className="edit-button" 
                                            title="Edit Suggestion"
                                            onClick={() => handleEdit(suggestion)}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button 
                                            className="delete-button" 
                                            title="Delete Suggestion"
                                            onClick={() => handleDelete(suggestion.id)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                </div>
                                <div className="suggestion-content">
                                    <p><strong>Query:</strong> {suggestion.query}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="manage-section">
                <h2>
                    <FontAwesomeIcon icon={faSettings} className="section-icon" />
                    Daire Options
                </h2>
                <p className="section-description">
                    Configure various Daire settings and options.
                </p>

                {error && <div className="error-message">{error}</div>}

                <div className="options-list">
                    {options.map(option => (
                        <div key={option.id} className="option-item">
                            <div className="option-header">
                                <h3>{option.title}</h3>
                                {editingOption?.id === option.id ? (
                                    <div className="option-actions">
                                        <button 
                                            className="save-button"
                                            onClick={handleOptionSave}
                                        >
                                            Save
                                        </button>
                                        <button 
                                            className="cancel-button"
                                            onClick={handleOptionCancel}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <button 
                                        className="edit-button"
                                        onClick={() => handleOptionEdit(option)}
                                    >
                                        <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                )}
                            </div>
                            {editingOption?.id === option.id ? (
                                <div className="option-edit">
                                    {option.title === 'Welcome' ? (
                                        <>
                                            <div className="welcome-edit-header">
                                                <label>Welcome Message</label>
                                                {renderPlaceholderHelp()}
                                            </div>
                                            <textarea
                                                value={optionValue}
                                                onChange={(e) => setOptionValue(e.target.value)}
                                                placeholder="Enter welcome message..."
                                            />
                                            <div className="welcome-preview">
                                                <strong>Preview:</strong> {formatWelcomeMessage(optionValue, { name: 'John Doe' })}
                                            </div>
                                        </>
                                    ) : option.title.toLowerCase().includes('disclaimer') ? (
                                        <>
                                           
                                            <ReactQuill
                                                value={optionValue}
                                                onChange={setOptionValue}
                                                modules={quillModules}
                                                formats={quillFormats}
                                                placeholder={`Enter ${option.title.toLowerCase()}...`}
                                                style={{ height: '200px', marginBottom: '50px' }}
                                            />
                                            <div className="disclaimer-preview">
                                                <strong>Preview:</strong>
                                                <div dangerouslySetInnerHTML={{ __html: optionValue }} />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <label>{option.title}</label>
                                            <input
                                                type="text"
                                                value={optionValue}
                                                onChange={(e) => setOptionValue(e.target.value)}
                                                placeholder={`Enter ${option.title.toLowerCase()}...`}
                                            />
                                            {option.title === 'Month' && (
                                                <div className="option-preview">
                                                    <strong>Current Value:</strong> {new Date().toLocaleString('default', { month: 'long' })}
                                                </div>
                                            )}
                                            {option.title === 'Year' && (
                                                <div className="option-preview">
                                                    <strong>Current Value:</strong> {new Date().getFullYear()}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <div className="option-actions">
                                        <button onClick={handleOptionSave}>Save</button>
                                        <button onClick={handleOptionCancel}>Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="option-value">
                                    {option.title === 'Welcome' ? (
                                        <div className="welcome-preview">
                                            {option.value}
                                        </div>
                                    ) : option.title.toLowerCase().includes('disclaimer') ? (
                                        <div className="disclaimer-display">
                                            <div dangerouslySetInnerHTML={{ __html: option.value }} />
                                        </div>
                                    ) : (
                                        option.value
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

DaireManage.propTypes = {
    currentUser: PropTypes.object
};

export default DaireManage; 